import {
   createAsyncThunk,
   createEntityAdapter,
   createSelector,
   createSlice,
} from "@reduxjs/toolkit"
import { ApiError, ApiResponse } from "@/api"
import {
   requestCreateSensor,
   requestDeleteSensor,
   requestSensors,
   requestUpdateSensor,
   SensorResponse,
   SensorsParams,
} from "@/api/SensorsService"
import { HttpCodes } from "@/constants/globals"
import type { RootState } from "@/store"
import {
   deleteFacilityLevelSensor,
   upsertFacilityLevelSensor,
} from "@/store/facilitiesSlice"
import { SensorForm } from "@/types/sensor"

const wearablesAdapter = createEntityAdapter<SensorResponse>()

interface SensorsState {
   loadingSensors: boolean
   errorSensors: number | null
   loadingCreateSensor: boolean
   errorCreateSensor: number | null
   loadingUpdateSensor: boolean
   errorUpdateSensor: number | null
   loadingDeleteSensor: boolean
   errorDeleteSensor: number | null
   sensorIdToEdit: string | null
   sensorIdToDelete: string | null
}

export const initialState: SensorsState &
   ReturnType<typeof wearablesAdapter.getInitialState> = {
   ...wearablesAdapter.getInitialState(),
   loadingSensors: false,
   errorSensors: null,
   loadingCreateSensor: false,
   errorCreateSensor: null,
   loadingUpdateSensor: false,
   errorUpdateSensor: null,
   loadingDeleteSensor: false,
   errorDeleteSensor: null,
   sensorIdToEdit: null,
   sensorIdToDelete: null,
}

export const fetchSensors = createAsyncThunk(
   "sensors/fetchSensors",
   async (params: SensorsParams, thunkAPI) => {
      try {
         const response: ApiResponse<SensorResponse[]> =
            await requestSensors(params)
         return response
      } catch (error) {
         if (error instanceof Error) {
            const apiError = error as unknown as ApiError
            return thunkAPI.rejectWithValue(apiError.status)
         }
         return thunkAPI.rejectWithValue(500)
      }
   }
)

export const createSensor = createAsyncThunk(
   "sensors/createSensor",
   async (sensorForm: Partial<SensorForm>[], thunkAPI) => {
      try {
         const response: ApiResponse<SensorResponse[]> =
            await requestCreateSensor(sensorForm)

         if (response.status === HttpCodes.CREATED) {
            response.data.forEach((sensor) => {
               thunkAPI.dispatch(
                  upsertFacilityLevelSensor({
                     id: sensor.id,
                     macAddress: sensor.macAddress,
                     serialNumber: sensor.serialNumber,
                     level: sensor.level,
                  })
               )
            })
         }

         return response
      } catch (error) {
         if (error instanceof Error) {
            const apiError = error as unknown as ApiError
            return thunkAPI.rejectWithValue(apiError.status)
         }
         return thunkAPI.rejectWithValue(500)
      }
   }
)

export const updateSensor = createAsyncThunk(
   "sensors/updateSensor",
   async (
      {
         sensorId,
         sensorForm,
      }: { sensorId: string; sensorForm: Partial<SensorForm> },
      thunkAPI
   ) => {
      try {
         const response: ApiResponse<SensorResponse> =
            await requestUpdateSensor(sensorId, sensorForm)
         if (response.status === HttpCodes.OK) {
            const sensor = response.data
            thunkAPI.dispatch(
               upsertFacilityLevelSensor({
                  id: sensor.id,
                  macAddress: sensor.macAddress,
                  serialNumber: sensor.serialNumber,
                  level: sensor.level,
               })
            )
         }
         return response
      } catch (error) {
         if (error instanceof Error) {
            const apiError = error as unknown as ApiError
            return thunkAPI.rejectWithValue(apiError.status)
         }
         return thunkAPI.rejectWithValue(500)
      }
   }
)

export const deleteSensor = createAsyncThunk(
   "sensors/deleteSensor",
   async (sensorId: string, thunkAPI) => {
      try {
         const response: ApiResponse<void> = await requestDeleteSensor(sensorId)

         if (response.status === HttpCodes.NO_CONTENT) {
            thunkAPI.dispatch(deleteFacilityLevelSensor({ id: sensorId }))
         }
         return sensorId
      } catch (error) {
         if (error instanceof Error) {
            const apiError = error as unknown as ApiError
            return thunkAPI.rejectWithValue(apiError.status)
         }
         return thunkAPI.rejectWithValue(500)
      }
   }
)

const sensorsSlice = createSlice({
   name: "sensors",
   initialState,
   reducers: {},
   extraReducers: (builder) => {
      builder
         .addCase(fetchSensors.pending, (state) => {
            state.loadingSensors = true
            state.errorSensors = null
         })
         .addCase(fetchSensors.fulfilled, (state, action) => {
            state.loadingSensors = false
            state.errorSensors = null
            wearablesAdapter.setAll(state, action.payload.data)
         })
         .addCase(fetchSensors.rejected, (state, action) => {
            state.loadingSensors = false
            state.errorSensors = action.payload as number
         })
         .addCase(createSensor.pending, (state) => {
            state.loadingCreateSensor = true
            state.errorCreateSensor = null
         })
         .addCase(createSensor.fulfilled, (state) => {
            state.loadingCreateSensor = false
            state.errorCreateSensor = null
         })
         .addCase(createSensor.rejected, (state, action) => {
            state.loadingCreateSensor = false
            state.errorCreateSensor = action.payload as number
         })
         .addCase(updateSensor.pending, (state) => {
            state.loadingUpdateSensor = true
            state.errorUpdateSensor = null
         })
         .addCase(updateSensor.fulfilled, (state) => {
            state.loadingUpdateSensor = false
            state.errorUpdateSensor = null
         })
         .addCase(updateSensor.rejected, (state, action) => {
            state.loadingUpdateSensor = false
            state.errorUpdateSensor = action.payload as number
         })
         .addCase(deleteSensor.pending, (state) => {
            state.loadingDeleteSensor = true
            state.errorDeleteSensor = null
         })
         .addCase(deleteSensor.fulfilled, (state) => {
            state.loadingDeleteSensor = false
            state.errorDeleteSensor = null
         })
         .addCase(deleteSensor.rejected, (state, action) => {
            state.loadingDeleteSensor = false
            state.errorDeleteSensor = action.payload as number
         })
   },
})

const sensorsAdapterSelectors = wearablesAdapter.getSelectors(
   (state: RootState) => state.sensorsReducer
)

export const selectAllWearables = sensorsAdapterSelectors.selectAll

export const selectWearableMacAddresses = createSelector(
   selectAllWearables,
   (wearables) => new Set(wearables.map((w) => w.macAddress))
)

export default sensorsSlice.reducer
